// ✅ src/pages/VehicleV2/VehicleTransferReport_Print.jsx
// FULL WORKING PRINT PAGE (React) - SAME STYLE AS YOUR VehicleStatusReport_Print.jsx
// - reads filters from URL query string
// - calls: GET http://nissi-india.com/api/vehicleTransferReport/print/main?... (recommended)
//   (If you don't have /print yet, you can point it to /list and read `rows`)
// - prints ONE table like your ASPX print screenshot
// - includes A4 portrait print CSS + Back + Print buttons

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/vehicleTransferReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  if (typeof isoOrDate === "string" && isoOrDate.includes("/")) return isoOrDate; // dd/MM/yyyy already
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "0.00";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Normalize rows to match your PRINT columns:
 * S.No | Vehicle Main Type | Vehicle Code | Reg No. | Transfer From | Transfer To |
 * Transfer Date | Transfer By | Appr. Date | Appr By | Recev. Date | Working Hr | Cumulative Hr
 */
function normalizeRow(x, idx) {
  return {
    sno: x?.sno ?? idx + 1,
    vehicleMainType: x?.vehicleMainType ?? x?.VehicleMainType ?? x?.fld_vehiclemaintype ?? "",
    vehicleCode: x?.vehicleCode ?? x?.VehicleCode ?? x?.fld_vehicle_code ?? "",
    vehicleRegNo: x?.vehicleRegNo ?? x?.VehicleRegNo ?? x?.fld_vehicle_regno ?? "",
    transferFrom: x?.transferFrom ?? x?.TransferFrom ?? "",
    transferTo: x?.transferTo ?? x?.TransferTo ?? "",
    transferDate: fmtDate(x?.transferDate ?? x?.TransferDate),
    transferBy: x?.transferBy ?? x?.TransferBy ?? "",
    approvedDate: fmtDate(x?.approvedDate ?? x?.ApprovedDate),
    approvedBy: x?.approvedBy ?? x?.ApprovedBy ?? "",
    recevDate: fmtDate(x?.fbRecDate ?? x?.recevDate ?? x?.RecevDate ?? x?.FBRecDate),
    workHr: fmtNum2(x?.workHr ?? x?.WorkHR ?? x?.WorkHr ?? 0),
    cumHr: fmtNum2(x?.cumHr ?? x?.CUMHR ?? x?.CumulativeHr ?? 0),
  };
}

/* ---------------- component ---------------- */
export default function VehicleTransferReportPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ Read filters from query string (send same from list page)
  // Example:
  // /vehicle/VehicleTransferReport_Print?type=B&fromBranchId=8&toBranchId=0&fromDate=2025-01-01&toDate=2025-01-31&vehicleName=&vehicleRegistrationNumber=&vehicleCode=&vehicleMainTypeId=0
  const params = useMemo(() => {
    const obj = Object.fromEntries([...searchParams.entries()]);
    return {
      type: (obj.type || "B").toUpperCase() === "L" ? "L" : "B",
      fromBranchId: obj.fromBranchId || "0",
      toBranchId: obj.toBranchId || "0",
      fromDate: obj.fromDate || "",
      toDate: obj.toDate || "",
      vehicleName: obj.vehicleName || "",
      vehicleRegistrationNumber: obj.vehicleRegistrationNumber || "",
      vehicleCode: obj.vehicleCode || "",
      vehicleMainTypeId: obj.vehicleMainTypeId || "0",
    };
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        /**
         * ✅ Recommended:
         * GET /api/vehicleTransferReport/print/main  -> { success:true, rows:[...] }
         *
         * If you haven't created /print/main,
         * you can call /list (SP) and it returns { success:true, rows:[...] } also.
         */
        const res = await axios.get(`${API_BASE}/list`, { params });

        const d = res?.data || {};
        const list = Array.isArray(d.rows) ? d.rows : [];
        if (!alive) return;

        setRows(list);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const data = useMemo(() => rows.map(normalizeRow), [rows]);

  // Totals (aspx footer shows total of WorkHr or CumHr sometimes; you can change)
  const totalWorkHr = useMemo(
    () => data.reduce((sum, r) => sum + Number(String(r.workHr).replace(/,/g, "")) || 0, 0),
    [data]
  );

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 8px; font-size: 9pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 8pt; }
          th, td { border: 1px solid #000 !important; padding: 3px !important; }
        }
        @page {
          size: A4 portrait;
          margin: 0.35in;
        }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Top Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header (same visual like your print screenshot) */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
              </div>
            </div>

            <div className="flex-1 text-center leading-tight">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi CIVIL CONSTRUCTION</div>
              <div className="text-[10px] mt-1">
                3/42-12, Nagappa Gounder St, kavundampalayam, Coimbatore - Tamil Nadu.
                <br />
                Phone No : 9003744644 &nbsp;&nbsp; infracosoft-implementa
              </div>
              <div className="text-sm font-bold underline mt-2">VEHICLE TRANSFER REPORT</div>
            </div>

            <div className="w-44 text-right text-[10px]">
              <div>INFRA SCREEN : B-46</div>
              <div>
                From: <b>{toStr(params.fromDate) || "-"}</b>
              </div>
              <div>
                To: <b>{toStr(params.toDate) || "-"}</b>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 35 }}>S.No</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 110 }}>Vehicle Main Type</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>Vehicle Code</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>Reg No.</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Transfer From</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Transfer To</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>Transfer Date</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>Transfer By</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 75 }}>Appr. Date</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 70 }}>Appr By</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 75 }}>Recev. Date</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 70 }}>Working Hr</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>Cumulative Hr</th>
              </tr>
            </thead>

            <tbody>
              {!data?.length ? (
                <tr>
                  <td colSpan={13} className="border border-black px-2 py-2 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                data.map((r) => (
                  <tr key={`${r.vehicleCode}-${r.sno}`}>
                    <td className="border border-black px-1 py-1 text-center">{r.sno}</td>
                    <td className="border border-black px-1 py-1">{r.vehicleMainType}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.vehicleCode}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.vehicleRegNo}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.transferFrom}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.transferTo}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.transferDate}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.transferBy}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.approvedDate}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.approvedBy}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.recevDate}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.workHr}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.cumHr}</td>
                  </tr>
                ))
              )}
            </tbody>

            {/* Footer total (optional) */}
            {data?.length ? (
              <tfoot>
                <tr>
                  <td colSpan={11} className="border border-black px-2 py-1 text-right font-bold">
                    Total
                  </td>
                  <td className="border border-black px-2 py-1 text-right font-bold">
                    {fmtNum2(totalWorkHr)}
                  </td>
                  <td className="border border-black px-2 py-1" />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        {/* Footer note */}
        <div className="mt-3 border-2 border-black p-1 text-xs text-center">
          Report Created by Infracosoft version 2.1. www.infracosoft.com
        </div>

        {/* Bottom Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}
